import { memo, useCallback, useEffect, useMemo } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import type { DialogProps } from '@mui/material';

import { gql } from '@graphql/__generated__';
import {
  type ImageGalleryItem,
  NewImageGallery,
} from '@modules/_shared/components/image-gallery/NewImageGallery';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useLocale } from '@shared/hooks/locale';

const GET_USER_IMAGES_FOR_GALLERY = gql(/* GraphQL */ `
  query GetUserImagesForGallery($userId: uuid!) {
    user_images(
      where: { user_id: { _eq: $userId } }
      order_by: { image: { created_at: desc } }
    ) {
      id
      is_primary
      image {
        id
        url
      }
    }
  }
`);

const GET_PROPERTY_IMAGES_FOR_GALLERY = gql(/* GraphQL */ `
  query GetPropertyImagesForGallery($propertyId: uuid!) {
    property_images(
      where: { property_id: { _eq: $propertyId } }
      order_by: { image: { created_at: desc } }
    ) {
      id
      image {
        id
        url
      }
    }
  }
`);

const GET_TEAM_IMAGES_FOR_GALLERY = gql(/* GraphQL */ `
  query GetTeamImagesForGallery($teamId: uuid!) {
    teams_by_pk(id: $teamId) {
      id
      logo_image_id
      cover_photo_image_id
      team_images(order_by: { image: { created_at: desc } }) {
        id
        order_nr
        image {
          id
          url
        }
      }
    }
  }
`);

const DELETE_USER_IMAGE_FROM_GALLERY = gql(/* GraphQL */ `
  mutation DeleteUserImageFromGallery($id: uuid!) {
    delete_user_images_by_pk(id: $id) {
      id
    }
  }
`);

const DELETE_TEAM_IMAGE_FROM_GALLERY = gql(/* GraphQL */ `
  mutation DeleteTeamImageFromGallery($id: uuid!) {
    delete_team_images_by_pk(id: $id) {
      id
    }
  }
`);

type ImageGalleryDialogProps = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  entityType: 'user' | 'property' | 'team';
  onSelectImage: (image: ImageGalleryItem) => void;
  onFilesUploaded: (images: { id: string; url: string }[]) => Promise<void>;
  maxWidth?: DialogProps['maxWidth'];
};

const ENTITY_CONFIG = {
  user: {
    query: GET_USER_IMAGES_FOR_GALLERY,
    dataKey: 'user_images' as const,
    variableKey: 'userId' as const,
    canDelete: true,
  },
  property: {
    query: GET_PROPERTY_IMAGES_FOR_GALLERY,
    dataKey: 'property_images' as const,
    variableKey: 'propertyId' as const,
    canDelete: false,
  },
  team: {
    query: GET_TEAM_IMAGES_FOR_GALLERY,
    dataKey: 'team_images' as const,
    variableKey: 'teamId' as const,
    canDelete: true,
  },
} as const;

export const ImageGalleryDialog = memo(
  ({
    open,
    onClose,
    entityId,
    entityType,
    onSelectImage,
    onFilesUploaded,
    maxWidth,
  }: ImageGalleryDialogProps) => {
    const { t } = useLocale();

    const userQuery = useQuery(GET_USER_IMAGES_FOR_GALLERY, {
      variables: { userId: entityId },
      skip: !open || entityType !== 'user',
    });

    const propertyQuery = useQuery(GET_PROPERTY_IMAGES_FOR_GALLERY, {
      variables: { propertyId: entityId },
      skip: !open || entityType !== 'property',
    });

    const teamQuery = useQuery(GET_TEAM_IMAGES_FOR_GALLERY, {
      variables: { teamId: entityId },
      skip: !open || entityType !== 'team',
    });

    const queries = {
      user: userQuery,
      property: propertyQuery,
      team: teamQuery,
    };

    const { data, loading, refetch } = queries[entityType];
    const config = ENTITY_CONFIG[entityType];

    const [deleteUserImage] = useMutation(DELETE_USER_IMAGE_FROM_GALLERY, {
      update: (cache, { data }) => {
        if (!data?.delete_user_images_by_pk) {
          return;
        }

        cache.evict({
          id: cache.identify({
            __typename: 'user_images',
            id: data.delete_user_images_by_pk.id,
          }),
        });
      },
    });

    const [deleteTeamImage] = useMutation(DELETE_TEAM_IMAGE_FROM_GALLERY, {
      update: (cache, { data }) => {
        if (!data?.delete_team_images_by_pk) {
          return;
        }

        cache.evict({
          id: cache.identify({
            __typename: 'team_images',
            id: data.delete_team_images_by_pk.id,
          }),
        });
      },
    });

    // Refetch when dialog opens to ensure fresh data
    useEffect(() => {
      if (open) {
        refetch();
      }
    }, [open, refetch]);

    const images = useMemo(() => {
      if (!data) {
        return [];
      }

      let items: any[] = [];
      let logoImageId: string | null = null;
      let coverPhotoImageId: string | null = null;

      if (entityType === 'user' && 'user_images' in data) {
        items = data.user_images;
      } else if (entityType === 'property' && 'property_images' in data) {
        items = data.property_images;
      } else if (
        entityType === 'team' &&
        'teams_by_pk' in data &&
        data.teams_by_pk
      ) {
        items = data.teams_by_pk.team_images ?? [];
        // Get the team's current logo and cover photo IDs
        logoImageId = data.teams_by_pk.logo_image_id ?? null;
        coverPhotoImageId = data.teams_by_pk.cover_photo_image_id ?? null;
      }

      return items.map((item, index) => {
        const imageId = item.image.id;

        // Determine if the image can be deleted
        let canDelete = true;

        if (entityType === 'user' && 'is_primary' in item) {
          // For user images, prevent deletion of primary image
          canDelete = !item.is_primary;
        } else if (entityType === 'team') {
          // For team images, prevent deletion if it's the current logo or banner
          canDelete = imageId !== logoImageId && imageId !== coverPhotoImageId;
        }

        return {
          id: item.id,
          // We do not need order_nr in this use case as there is no re-order logic
          // It can also be null, so we use index as fallback
          order_nr: item.order_nr ?? index,
          image: {
            id: imageId,
            url: item.image.url ?? '',
          },
          canDelete,
        };
      });
    }, [data, entityType]);

    const handleSelectImage = useCallback(
      (image: ImageGalleryItem) => {
        onSelectImage(image);
      },
      [onSelectImage],
    );

    const handleFilesUploaded = useCallback(
      async (uploadedFiles: { id: string; url: string }[]) => {
        await onFilesUploaded(uploadedFiles);
        // Refetch after upload to show new images
        await refetch();
      },
      [onFilesUploaded, refetch],
    );

    const handleDeleteImage = useCallback(
      async (imageId: string) => {
        if (!config.canDelete || !data) {
          return;
        }

        try {
          if (entityType === 'user') {
            await deleteUserImage({
              variables: { id: imageId },
            });
          } else if (entityType === 'team') {
            await deleteTeamImage({
              variables: { id: imageId },
            });
          }
        } catch (error) {
          console.error('Failed to delete image:', error);
        }
      },
      [config.canDelete, data, entityType, deleteUserImage, deleteTeamImage],
    );

    return (
      <SlideDialog
        open={open}
        onClose={onClose}
        title={t('Choose from gallery')}
        maxWidth={maxWidth ?? 'lg'}
        fullWidth
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <NewImageGallery
            images={images}
            viewMode="grid"
            onSelectImage={handleSelectImage}
            onFilesUploaded={handleFilesUploaded}
            onDeleteImage={config.canDelete ? handleDeleteImage : undefined}
          />
        )}
      </SlideDialog>
    );
  },
);
